/* ------------------------------------------------------------------
   Replacement for: assets/newsletter.js
   Theme: PHASE 2 | GENERATIONAL WEALTH

   What changed and why
   --------------------
   The original script intercepted the newsletter form's submit event,
   called preventDefault() + stopImmediatePropagation(), and then fired
   form.submit() itself. That is the problem:

     - stopImmediatePropagation() kills every other submit listener on
       the form, including the one Shopify's captcha (hCaptcha) attaches.
     - form.submit() submits the form WITHOUT firing submit listeners at
       all, so the captcha token is never attached to the post.

   Shopify then receives a customer form post with no valid captcha
   token and bounces the visitor to a challenge / error page.

   This version does the same job (remembering which form was submitted
   so the right success message shows after the page reloads) but lets
   the browser and Shopify's captcha handle the submit normally.

   Also fixed:
     - sessionStorage is wrapped in try/catch (it throws in Safari
       private browsing and with blocked site data, which silently broke
       the form).
     - scrollToForm referenced a bare `theme` global; now guarded.
     - success check no longer requires customer_posted to be the FIRST
       query parameter.
   ------------------------------------------------------------------ */

(function () {
  'use strict';

  var FORM_SELECTOR = '[data-newsletter-form]';
  var HEADING_SELECTOR = '[data-newsletter-heading]';
  var SUCCESS_CLASS = 'has-success';
  var ERROR_CLASS = 'has-error';
  var HIDDEN_CLASS = 'hidden';
  var STORAGE_KEY = 'newsletter_form_id';

  function readStorage() {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function writeStorage(value) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      /* storage blocked - not fatal, we just lose the success message */
    }
  }

  function removeStorage() {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* no-op */
    }
  }

  if (customElements.get('newsletter-component')) return;

  customElements.define(
    'newsletter-component',
    class extends HTMLElement {
      constructor() {
        super();
        this.newsletter = this.querySelector(FORM_SELECTOR);
        this.formID = null;
      }

      connectedCallback() {
        if (!this.newsletter) return;
        if (window.location.pathname === '/challenge') return;

        this.newsletterSubmit = function (event) {
          this.newsletterSubmitEvent(event);
        }.bind(this);

        this.newsletter.addEventListener('submit', this.newsletterSubmit);
        this.showMessage();
      }

      disconnectedCallback() {
        if (this.newsletter && this.newsletterSubmit) {
          this.newsletter.removeEventListener('submit', this.newsletterSubmit);
        }
      }

      /* Note which form was submitted, then get out of the way.
         No preventDefault, no stopImmediatePropagation, no form.submit() -
         the captcha needs the event to run its course. */
      newsletterSubmitEvent() {
        removeStorage();
        writeStorage(this.newsletter.id);
      }

      showMessage() {
        this.formID = readStorage();
        if (!this.formID || this.newsletter.id !== this.formID) return;

        var form = this.newsletter;
        var heading = form.parentElement
          ? form.parentElement.querySelector(HEADING_SELECTOR)
          : null;

        var search = window.location.search;
        var posted = search.indexOf('customer_posted=true') !== -1;
        var rejected = search.indexOf('accepts_marketing') !== -1;

        if (posted) {
          form.classList.remove(ERROR_CLASS);
          form.classList.add(SUCCESS_CLASS);
        } else if (rejected) {
          form.classList.remove(SUCCESS_CLASS);
          form.classList.add(ERROR_CLASS);
        }

        if (posted || rejected) {
          if (heading) heading.classList.add(HIDDEN_CLASS);
          form.classList.remove(HIDDEN_CLASS);
          removeStorage();

          var self = this;
          window.addEventListener('load', function () {
            self.scrollToForm(form);
          });
        }
      }

      scrollToForm(form) {
        var t = window.theme;
        if (!t || typeof t.scrollTo !== 'function') return;

        var rect = form.getBoundingClientRect();
        var viewportHeight = t.windowHeight || window.innerHeight;
        var viewportWidth = t.windowWidth || window.innerWidth;

        var inView =
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= viewportHeight &&
          rect.right <= viewportWidth;

        if (inView) return;

        setTimeout(function () {
          t.scrollTo(form.getBoundingClientRect().top);
        }, 500);
      }
    }
  );
})();